

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewCustomerCouponModal from 'components/NewCustomerCouponModal';

const CustomerCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('customer_coupons');

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/customer-coupons/');
            setCoupons(response.data);
        } catch (error) {
            console.error('Error fetching customer coupons:', error);
            setSnackbar({ open: true, message: 'Error loading customer coupons', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveCoupon = async (payload) => {
        try {
            await axiosInstance.post('/customer-coupons/', payload);
            setSnackbar({ open: true, message: 'Customer Coupon created successfully', severity: 'success' });
            fetchCoupons();
        } catch (error) {
            console.error('Error saving customer coupon:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/customer-coupon/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Customer",
            accessor: "customer_id",
            width: "20%",
            Cell: ({ row }) => row.original.customer?.name || '-'
        },
        {
            Header: "Coupon",
            accessor: "coupon_id",
            width: "20%",
            Cell: ({ row }) => row.original.coupon?.name || '-'
        },
        {
            Header: "Amount",
            accessor: "amount",
            width: "12%",
            Cell: ({ value }) => value ? `£${value}` : '-'
        },
        {
            Header: "Available Amount",
            accessor: "available_amount",
            width: "12%",
            Cell: ({ value }) => value ? `£${value}` : '-'
        },
        {
            Header: "Currency",
            accessor: "currency",
            width: "8%"
        },
        {
            Header: "Expiry Date",
            accessor: "expiry_date",
            width: "13%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        }
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: coupons,
    }), [coupons]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Customer Coupons</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Customer Coupon
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : coupons.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Customer Coupon" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewCustomerCouponModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveCoupon}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default CustomerCoupons;

